"use client";
import { useFocusable } from "../focusable/focusable.js";
import { useCollectionItem } from "../collection/collection-item.js";
import { useDisclosureContent } from "../disclosure/disclosure-content.js";
import { useDisclosureStore } from "../disclosure/disclosure-store.js";
import { TabScopedContextProvider, useTabProviderContext } from "./tab-context.js";
import { createElement, createHook, forwardRef, useEvent, useId, useMergeRefs, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { getFirstTabbableIn, invariant } from "@ariakit/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/tab/tab-panel.tsx
const TagName = "div";
/**
* Returns props to create a `TabPanel` component.
* @see https://ariakit.com/components/tab
* @example
* ```jsx
* const store = useTabStore();
* const props = useTabPanel({ store });
* <TabList store={store}>
*   <Tab>Tab 1</Tab>
* </TabList>
* <Role {...props}>Panel 1</Role>
* ```
*/
const useTabPanel = createHook(function useTabPanel({ store, unmountOnHide, tabId: tabIdProp, getItem: getItemProp, scrollRestoration, scrollElement, ...props }) {
	const context = useTabProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "TabPanel must receive a `store` prop or be wrapped in a TabProvider component.");
	const ref = useRef(null);
	const id = useId(props.id);
	const tabId = useStoreState(store.panels, () => tabIdProp || store?.panels.item(id)?.tabId);
	const disclosure = useDisclosureStore({ open: useStoreState(store, (state) => !!tabId && state.selectedId === tabId) });
	const mounted = useStoreState(disclosure, "mounted");
	const scrollPositionRef = useRef(/* @__PURE__ */ new Map());
	const getScrollElement = useEvent(() => {
		const panelElement = ref.current;
		if (!panelElement) return null;
		if (!scrollElement) return panelElement;
		if (typeof scrollElement === "function") return scrollElement(panelElement);
		if ("current" in scrollElement) return scrollElement.current;
		return scrollElement;
	});
	useEffect(() => {
		if (!scrollRestoration) return;
		if (!mounted) return;
		const element = getScrollElement();
		if (!element) return;
		if (scrollRestoration === "reset") {
			element.scroll(0, 0);
			return;
		}
		if (!tabId) return;
		const position = scrollPositionRef.current.get(tabId);
		element.scroll(position?.x ?? 0, position?.y ?? 0);
		const onScroll = () => {
			scrollPositionRef.current.set(tabId, {
				x: element.scrollLeft,
				y: element.scrollTop
			});
		};
		element.addEventListener("scroll", onScroll);
		return () => {
			element.removeEventListener("scroll", onScroll);
		};
	}, [
		scrollRestoration,
		mounted,
		tabId,
		getScrollElement
	]);
	const [hasTabbableChildren, setHasTabbableChildren] = useState(false);
	useSafeLayoutEffect(() => {
		if (!mounted) return;
		const element = ref.current;
		if (!element) return;
		setHasTabbableChildren(!!getFirstTabbableIn(element));
	}, [mounted, tabId]);
	const getItem = useCallback((item) => {
		const nextItem = {
			...item,
			id: id || item.id,
			tabId: tabIdProp
		};
		if (getItemProp) return getItemProp(nextItem);
		return nextItem;
	}, [
		id,
		tabIdProp,
		getItemProp
	]);
	const onKeyDownProp = props.onKeyDown;
	const onKeyDown = useEvent((event) => {
		onKeyDownProp?.(event);
		if (event.defaultPrevented) return;
		if (!store?.composite) return;
		const action = {
			ArrowLeft: store.previous,
			ArrowRight: store.next,
			Home: store.first,
			End: store.last
		}[event.key];
		if (!action) return;
		const { selectedId } = store.getState();
		const nextId = action({ activeId: selectedId });
		if (!nextId) return;
		event.preventDefault();
		store.move(nextId);
	});
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(TabScopedContextProvider, {
		value: store,
		children: element
	}), [store]);
	props = {
		role: "tabpanel",
		"aria-labelledby": props["aria-label"] != null ? void 0 : tabId || void 0,
		...props,
		id,
		children: unmountOnHide && !mounted ? null : props.children,
		ref: useMergeRefs(ref, props.ref),
		onKeyDown
	};
	props = useFocusable({
		focusable: !store.composite && !hasTabbableChildren,
		...props
	});
	props = useDisclosureContent({
		store: disclosure,
		...props
	});
	props = useCollectionItem({
		store: store.panels,
		...props,
		getItem
	});
	return props;
});
/**
* Renders a tab panel element that's controlled by a
* [`Tab`](https://ariakit.com/reference/tab) component.
*
* If the [`tabId`](https://ariakit.com/reference/tab-panel#tabid) prop isn't
* provided, the tab panel will automatically associate with a
* [`Tab`](https://ariakit.com/reference/tab) based on its position in the DOM.
* Alternatively, you can render a single tab panel with a dynamic
* [`tabId`](https://ariakit.com/reference/tab-panel#tabid) value pointing to
* the selected tab.
* @see https://ariakit.com/components/tab
* @example
* ```jsx {6,7}
* <TabProvider>
*   <TabList>
*     <Tab>Tab 1</Tab>
*     <Tab>Tab 2</Tab>
*   </TabList>
*   <TabPanel>Panel 1</TabPanel>
*   <TabPanel>Panel 2</TabPanel>
* </TabProvider>
* ```
*/
const TabPanel = forwardRef(function TabPanel(props) {
	return createElement(TagName, useTabPanel(props));
});
//#endregion
export { TabPanel, useTabPanel };

//# sourceMappingURL=tab-panel.js.map